import React, { useCallback, useMemo } from 'react';
import type { TNotificationEvent, TNotificationInit, TNotificationUid } from '@shared/notifications/lib';
import { ENotificationType } from '@shared/notifications/lib';
import { Uid } from '@shared/lib/helpers';
import { NotificationsContext } from '@shared/notifications/model/useNotifications';
export type TNotificationsProviderProps = {
  defaultTitle?: string;
  children: React.ReactNode;
};
export function NotificationsProvider({
  defaultTitle = 'Notification',
  children
}: TNotificationsProviderProps) {
  const [notifications, setNotifications] = React.useState<Map<TNotificationUid, TNotificationEvent>>(new Map());
  const closeNotification = useCallback((uid: TNotificationUid) => {
    setNotifications(currState => {
      const copy = new Map(currState);
      copy.delete(uid);
      return copy;
    });
  }, []);
  const showNotification = useCallback((init: TNotificationInit) => {
    setNotifications(currState => {
      const uid = init.uid || Uid();
      const event: TNotificationEvent = {
        ...init,
        uid,
        title: init.title ?? defaultTitle,
        type: init.type ?? ENotificationType.info,
        close: () => closeNotification(uid)
      };
      return new Map(currState).set(uid, event);
    });
  }, [closeNotification, defaultTitle]);
  const value = useMemo(() => ({
    notifications,
    clearNotifications: () => notifications.clear(),
    closeNotification,
    showNotification
  }), [notifications, closeNotification, showNotification]);
  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}